<template>
  <b-form-group
    label="ประวัติการชำระเงิน"
    label-cols-lg="3"
    label-size="lg"
    label-class="pt-0"
  >
    <b-row class="justify-content-center">
      <b-col lg="10">
        <b-card class="payment-history-card shadow-sm">
          <div class="payment-history-header d-flex align-items-center mb-4">
            <div class="payment-history-icon mr-3">
              <i class="fas fa-history"></i>
            </div>
            <div>
              <h5 class="mb-1">ประวัติการชำระเงิน</h5>
              <p class="text-muted mb-0">ข้อมูลการชำระเงินย้อนหลัง</p>
            </div>
          </div>

          <div v-if="isLoading" class="text-center py-4">
            <b-spinner label="กำลังโหลด..."></b-spinner>
            <p class="mt-2">กำลังโหลดข้อมูล</p>
          </div>

          <div v-else-if="paymentHistory.length > 0" class="payment-history-list">
            <b-table
              striped
              hover
              responsive
              :items="paymentHistory"
              :fields="paymentHistoryFields"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :per-page="perPage"
              :current-page="currentPage"
              class="payment-history-table"
            >
              <template #cell(index)="data">
                {{ (currentPage - 1) * perPage + data.index + 1 }}
              </template>

              <template #cell(createdAt)="data">
                {{ formatDate(data.item.createdAt) }}
              </template>

              <template #cell(amount)="data">
                <span class="font-weight-bold">{{ data.item.amount }} บาท</span>
              </template>

              <template #cell(status)="data">
                <b-badge
                  :variant="getStatusVariant(data.item.status)"
                  pill
                  class="px-3 py-2"
                >
                  {{ getStatusText(data.item.status) }}
                </b-badge>
              </template>

              <template #cell(paymentMethod)="data">
                <span class="text-nowrap">
                  <i class="fas fa-credit-card mr-1"></i>
                  {{ getPaymentMethodText(data.item.paymentMethod) }}
                </span>
              </template>

              <template #cell(description)="data">
                <span class="text-truncate d-inline-block" style="max-width: 200px">
                  {{ data.item.description }}
                </span>
              </template>
            </b-table>

            <div class="d-flex justify-content-between align-items-center mt-3">
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                aria-controls="payment-history-table"
                align="center"
                class="mb-0"
              ></b-pagination>

              <b-form-select
                v-model="perPage"
                :options="perPageOptions"
                size="sm"
                class="w-auto"
              ></b-form-select>
            </div>
          </div>

          <div v-else class="payment-history-empty text-center py-4">
            <div class="empty-icon mb-3">
              <i class="fas fa-receipt text-muted"></i>
            </div>
            <h5 class="mb-2">ไม่พบประวัติการชำระเงิน</h5>
            <p class="text-muted">ยังไม่มีรายการชำระเงินในขณะนี้</p>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </b-form-group>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";

export default {
  name: "PaymentHistory",
  data() {
    return {
      // Table fields and configuration
      paymentHistoryFields: [
        { key: 'index', label: 'ลำดับ', class: 'text-center' },
        { key: 'createdAt', label: 'วันที่', sortable: true },
        { key: 'amount', label: 'จำนวนเงิน', sortable: true },
        { key: 'status', label: 'สถานะ', sortable: true },
        { key: 'paymentMethod', label: 'ช่องทางชำระเงิน' },
        { key: 'description', label: 'รายละเอียด' }
      ],
      // Pagination and sorting properties
      currentPage: 1,
      perPage: 10,
      perPageOptions: [
        { value: 5, text: '5 รายการ' },
        { value: 10, text: '10 รายการ' },
        { value: 15, text: '15 รายการ' },
        { value: 20, text: '20 รายการ' }
      ],
      sortBy: 'createdAt',
      sortDesc: true,
      isLoading: false,
      initialized: false
    };
  },
  computed: {
    ...mapGetters({
      paymentHistory: "modulePayment/getPaymentHistory",
      paymentHistoryTotal: "modulePayment/getPaymentHistoryTotal",
      paymentHistoryLoading: "modulePayment/getPaymentHistoryLoading",
    }),
    totalRows() {
      return this.paymentHistory.length;
    }
  },
  watch: {
    // Watchers for pagination and sorting
    currentPage() {
      this.loadPaymentHistory();
    },
    perPage() {
      this.currentPage = 1; // Reset to first page when changing page size
      this.loadPaymentHistory();
    },
    sortBy() {
      this.loadPaymentHistory();
    },
    sortDesc() {
      this.loadPaymentHistory();
    }
  },
  methods: {
    ...mapActions({
      fetchPaymentHistory: "modulePayment/fetchPaymentHistory",
    }),
    loadPaymentHistory() {
      if (!this.initialized) return; // Skip initial load
      
      const { clinicUrl, branchUrl } = this.$route.params;
      if (!clinicUrl || !branchUrl) {
        return;
      }

      this.isLoading = true;

      try {
        // Convert sort settings to API parameters
        const sort = this.sortBy;
        const order = this.sortDesc ? 'desc' : 'asc';
        const offset = (this.currentPage - 1) * this.perPage;
        const limit = this.perPage;

        this.fetchPaymentHistory({
          clinicUrl,
          branchUrl,
          sort,
          order,
          offset,
          limit
        }).finally(() => {
          this.isLoading = false;
        });

        // The store will now handle setting the total count
      } catch (error) {
        console.error("Error fetching payment history:", error);
        this.showDialogToast("danger", `ไม่สามารถดึงข้อมูลประวัติการชำระเงินได้`);
        this.isLoading = false;
      }
    },
    formatDate(dateString) {
      return moment(dateString).locale("th").format("LLL");
    },
    getStatusVariant(status) {
      const statusMap = {
        success: 'success',
        pending: 'warning',
        failed: 'danger',
        cancelled: 'secondary',
        expired: 'secondary'
      };
      return statusMap[status] || 'secondary';
    },
    getStatusText(status) {
      const statusMap = {
        success: 'สำเร็จ',
        pending: 'รอดำเนินการ',
        failed: 'ล้มเหลว',
        cancelled: 'ยกเลิก',
        expired: 'หมดอายุ'
      };
      return statusMap[status] || status;
    },
    getPaymentMethodText(method) {
      if (!method) return '-';
      const methodMap = {
        'ktb_fast_pay': 'KTB Fast Pay',
        'credit_card': 'บัตรเครดิต',
        'bank_transfer': 'โอนเงิน',
        'prompt_pay': 'พร้อมเพย์',
        'JCB': 'JCB',
        'VISA': 'VISA',
        'Master': 'MasterCard',
        'ITMX': 'PromptPay'
      };
      return methodMap[method] || method;
    },
    showDialogToast(variant, message) {
      if (this.$refs.Dialog) {
        this.$refs.Dialog.showToast(variant, message);
      } else {
        console.error(message);
      }
    },
    initialize() {
      this.initialized = true;
      this.loadPaymentHistory();
    }
  }
};
</script>

<style scoped>
/* Payment History Card Styling */
.payment-history-card {
  border: none;
  border-radius: 1rem;
  background: linear-gradient(145deg, #ffffff, #f8f9fa);
  overflow: hidden;
  transition: all 0.3s ease;
}

.payment-history-icon {
  font-size: 2.5rem;
  color: #6c757d;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(108, 117, 125, 0.1);
  border-radius: 50%;
}

.payment-history-table {
  margin-top: 1rem;
}

.payment-history-table th {
  font-weight: 600;
  background-color: rgba(52, 144, 220, 0.05);
  border-bottom: 2px solid #3490dc;
}

.payment-history-table td {
  vertical-align: middle;
}

.payment-history-empty .empty-icon {
  font-size: 4rem;
  color: #dee2e6;
  animation: pulse 3s infinite;
}

@keyframes pulse {
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.6;
  }
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .payment-history-icon {
    font-size: 2rem;
    width: 50px;
    height: 50px;
  }

  .payment-history-table {
    font-size: 0.9rem;
  }
}
</style>